import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { db } from '../firebaseConfig';
import { useDispatch, useSelector } from 'react-redux';
import {
  createKnowledgeBase,
  setKnowledgeBase,
} from '../redux/slices/knowledgeBaseSlice';
import { useTranslation } from 'react-i18next';
import KnowledgeBaseList from '../components/knowledgeBase/KnowledgeBaseList';

const types = ['guide', 'faq', 'tutorial'];
const matches = [
  'id:id',
  'targetId:targetId',
  'targetProfileId:targetProfileId',
  'dependencyId:dependencyId',
];
const collections = [
  'contacts',
  'cardsinvoiced',
  'cardsuninvoiced',
  'cardsexpense',
  'grids',
  'profiles',
  'services',
  'articles',
  'items',
  'payments',
  'storages',
  'nodies',
  'tasks',
];

const KnowledgeBasePage = () => {
  const dispatch = useDispatch();
  const { knowledgeBase } = useSelector((state) => state.knowledgeBase);
  const { query: searchTerm } = useSelector((state) => state.search);
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'article',
    match: 'exact',
    collection: 'tickets',
    content: '',
  });

  useEffect(() => {
    fetchKnowledgeBase();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      const kbRef = collection(db, 'knowledgeBase');
      const q = query(kbRef);
      const querySnapshot = await getDocs(q);
      const kbData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch(setKnowledgeBase(kbData));
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
    }
  };

  const handleCreateItem = async () => {
    try {
      await dispatch(createKnowledgeBase(formData));
      resetForm();
    } catch (error) {
      console.error('Error creating knowledge base item:', error);
    }
  };

  const handleUpdateItem = async () => {
    try {
      const itemRef = doc(db, 'knowledgeBase', selectedItem.id);
      await updateDoc(itemRef, {
        ...formData,
        updatedAt: new Date(),
      });
      fetchKnowledgeBase();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating knowledge base item:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'article',
      match: 'exact',
      collection: 'tickets',
      content: '',
    });
    setSelectedItem(null);
    setIsEditing(false);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setFormData(item);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = () => {
    if (selectedItem) {
      handleUpdateItem();
    } else {
      handleCreateItem();
    }
  };

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', display: 'flex' }}>
      <Box sx={{ width: '40%', overflow: 'auto', height: '100%' }}>
        <KnowledgeBaseList
          items={knowledgeBase}
          onSelect={handleSelectItem}
          searchTerm={searchTerm}
        />
      </Box>

      {/* Right side - Content and Details */}
      <Box sx={{ width: '60%', pl: 2, overflow: 'auto' }}>
        <Paper sx={{ p: 3, width: '100%', height: '100%' }}>
          {selectedItem ? (
            <>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}
              >
                <Typography variant="h6">
                  {isEditing ? t('knowledgeBase.editItem') : formData.name}
                </Typography>
                {!isEditing ? (
                  <IconButton onClick={handleEditClick}>
                    <EditIcon />
                  </IconButton>
                ) : (
                  <IconButton onClick={handleSaveClick}>
                    <SaveIcon />
                  </IconButton>
                )}
              </Box>

              {isEditing ? (
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      label={t('knowledgeBase.name')}
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label={t('knowledgeBase.description')}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sx={{ width: '30%' }}>
                    <FormControl fullWidth>
                      <InputLabel>{t('knowledgeBase.type')}</InputLabel>
                      <Select
                        value={formData.type}
                        label={t('knowledgeBase.type')}
                        onChange={(e) =>
                          setFormData({ ...formData, type: e.target.value })
                        }
                      >
                        {types.map((type) => (
                          <MenuItem key={type} value={type}>
                            {t(`knowledgeBase.types.${type}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sx={{ width: '30%' }}>
                    <FormControl fullWidth>
                      <InputLabel>{t('knowledgeBase.match')}</InputLabel>
                      <Select
                        value={formData.match}
                        label={t('knowledgeBase.match')}
                        onChange={(e) =>
                          setFormData({ ...formData, match: e.target.value })
                        }
                      >
                        {matches.map((match) => (
                          <MenuItem key={match} value={match}>
                            {t(`knowledgeBase.matches.${match}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sx={{ width: '30%' }}>
                    <FormControl fullWidth>
                      <InputLabel>{t('knowledgeBase.collection')}</InputLabel>
                      <Select
                        value={formData.collection}
                        label={t('knowledgeBase.collection')}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            collection: e.target.value,
                          })
                        }
                      >
                        {collections.map((collection) => (
                          <MenuItem key={collection} value={collection}>
                            {t(`knowledgeBase.collections.${collection}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : null}

              <Divider sx={{ my: 2 }} />

              {isEditing ? (
                <Box sx={{ height: 500 }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.content}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setFormData({ ...formData, content: data });
                    }}
                    config={{
                      toolbar: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        '|',
                        'blockQuote',
                        'insertTable',
                        'undo',
                        'redo',
                      ],
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 500,
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                {t('knowledgeBase.selectItem')}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default KnowledgeBasePage;
